// ── Rich-text font sizes (selection-level) ─────────────────────────────────
// The owner sizes PARTS of a slide title/description (a word, a phrase, or
// the whole field) from the rich-text editor's Text Size dropdown. The
// styling is stored inside the limited-HTML rich text as
// `<font size="<n>">…</font>` — the exact shape execCommand("fontSize")
// emits — and shared/richText.ts allowlists ONLY the legacy size numbers
// below. The legacy browser 1–7 scale is NOT what renders: both the editor
// surface (client/src/index.css) and the public page
// (vault-template.css + VAULT_CRITICAL_CSS in server/publicHead.ts) carry a
// CSS rule per size mapping it to a relative em so the editor and the live
// page always match — see the lockstep comments there.

export interface RichTextSizeOption {
  /** Exact legacy size number stored in the size="" attribute. */
  size: string;
  /** Owner-facing label in the editor's Text Size dropdown. */
  label: string;
  /** CSS font-size (relative, so it scales with the surrounding heading
   *  or body context) applied by the render rules. */
  em: number;
}

export const RICH_TEXT_SIZE_OPTIONS: readonly RichTextSizeOption[] = [
  { size: "2", label: "Small", em: 0.85 },
  // "Normal" resets to the surrounding text's size (execCommand can't unwrap
  // an existing <font size>, so it re-wraps at the neutral 1em step).
  { size: "3", label: "Normal", em: 1 },
  { size: "5", label: "Large", em: 1.25 },
  { size: "6", label: "Extra Large", em: 1.5 },
  { size: "4", label: "H2", em: 1.75 },
  { size: "7", label: "H1", em: 2 },
] as const;

// The single numeric size palette used by every rich-text editor.
// Legacy named sizes above remain read-compatible for existing content only.
export const TEXT_SIZE_OPTIONS = [
  8, 9, 10, 11, 12, 14, 15, 16, 18, 20, 22, 24, 28, 32, 36,
  40, 44, 48, 56, 64, 72, 80, 88, 96, 112, 128, 160, 200,
].map((px) => ({ size: `${px}px`, label: `${px} px`, px }));

export const TEXT_MIN_SIZE = 8;
export const TEXT_MAX_SIZE = 200;
export const HERO_HEADLINE_MIN_SIZE = 40;

export function isEditableTextSize(value: string): boolean {
  if (!/^\d+px$/.test(value)) return false;
  const px = Number(value.slice(0, -2));
  return px >= TEXT_MIN_SIZE && px <= TEXT_MAX_SIZE;
}

/** Saved-content policy shared by editor, website, article and email sanitizers.
 * Retain historical fractional article sizes and Hero-relative sizes on read.
 */
export function isSafeTextSize(value: string): boolean {
  return value === "0.55em" || value === "0.7em" || isEditableTextSize(value) ||
    (/^\d+\.\d+px$/.test(value) && Number.parseFloat(value) >= TEXT_MIN_SIZE && Number.parseFloat(value) <= 96);
}

/** Exact allowlisted size numbers (the only values valid in size=""). */
export const RICH_TEXT_SIZE_VALUES: readonly string[] = RICH_TEXT_SIZE_OPTIONS.map(
  (o) => o.size,
);

/** Lookup: size attribute value → option (or null when not allowlisted). */
export function findRichTextSize(size: unknown): RichTextSizeOption | null {
  if (typeof size !== "string") return null;
  const s = size.trim();
  return RICH_TEXT_SIZE_OPTIONS.find((o) => o.size === s) ?? null;
}
